// @ts-expect-error - api artifacts are generated and should be correct, we just need to ignore type checking for them
// eslint-disable-next-line import/no-useless-path-segments
import apiArtifacts from '.spec/self-care/apiArtifacts';
import { ApolloClient, ApolloLink, InMemoryCache } from '@apollo/client';
import type { NormalizedCacheObject, TypePolicy } from '@apollo/client';
import { Capacitor } from '@capacitor/core';
import apolloLogger from 'apollo-link-logger';
import { LocalStorageWrapper, persistCache } from 'apollo3-cache-persist';
import { debounce } from 'lodash';
import { filter, isEmpty } from 'lodash/fp';
import type { OpenAPIV3 } from 'openapi-types';
import store from 'store2';

import { LANGUAGE } from '@skippay/core/constants/queryAndStorageKeys';
import { captureException } from '@skippay/core/global/errorTracking';
import { AppLanguage } from '@skippay/core/i18n/languages';
import { selectionSetToFragment, wrapInDocument } from '@skippay/core/utils/gqlAstUtils';
import {
	createApolloLink,
	createAxiosHttpAgent,
	possibleTypesFromApiArtifacts,
} from '@skippay/core/utils/gqlEngineUtils';
import type { RequestMiddleware } from '@skippay/core/utils/gqlEngineUtils';
import { memoPendingRequestsMiddleware } from '@skippay/core/utils/gqlMemoPendingRequests';
import { xFieldsRequestMiddleware } from '@skippay/core/utils/gqlToXFields';
import { gqlXLanguageMiddleware } from '@skippay/core/utils/gqlToXLanguage';
import { mergeFnPaginatedData } from '@skippay/core/utils/gqlUtils';
import { oasValidationMiddleware } from '@skippay/core/utils/gqlValidator';
import matchesFilter from '@skippay/core/utils/matchesFilter';
import type { ApiArtifacts } from '@skippay/oas-to-gql-schema/src/types';
import { Widget } from '@skippay/widget';

import { STORAGE_KEY_PERSIST_APOLLO_CACHE } from 'constants/queryAndStorageKeys';
import AccountAuthorizationFragment from 'fragments/AccountAuthorizationFragment.graphql';
import AmountFragment from 'fragments/AmountFragment.graphql';
import BillingDefermentFragment from 'fragments/BillingDefermentFragment.graphql';
import BillingFragment from 'fragments/BillingFragment.graphql';
import PromocodeOfferFragment from 'fragments/PromocodeOfferFragment.graphql';
import { getAuthorizationApolloLink } from 'modules/auth/utils/accessTokenUtils';

import resolvers from './resolvers';

interface CreateApolloClientConfig {
	accessToken?: string;
	language?: AppLanguage;
}

const ssr = typeof window === `undefined`;

/**
 * This typePolicy defines a singleton entity - one such only single one ever exists in out application.
 */
const singletonTypePolicy: TypePolicy = {
	// the singleton has no keyFields
	keyFields: [],
	// whenever the entity returns from the BE, merge its new result with old data
	merge: true,
};

const debouncedWidgetUpdate = debounce(() => Widget?.reloadWidget(), 2000);
const authFilterCache = new WeakMap<readonly unknown[], Map<string, unknown>>();

const maxSize = 1048576; // 1 MB

const cache = new InMemoryCache({
	resultCacheMaxSize: maxSize,
	typePolicies: {
		Billing_additionalInfoDeferment: {
			merge: true,
		},
		Contract: singletonTypePolicy,
		coinsOverview: singletonTypePolicy,
		InvestmentsAccountResponse: singletonTypePolicy,
		NotificationSettings: singletonTypePolicy,
		Billing: {
			fields: {
				paymentAvailability: {
					merge(existing, incoming) {
						return {
							...existing,
							...incoming,
							reasons: incoming?.reasons || existing?.reasons,
						};
					},
				},
			},
		},
		PagingInfo: {
			keyFields: false,
		},
		Query: {
			fields: {
				accountAuthorizations: {
					read(cached, { args, readField }) {
						// The client `matchesFilter` is necessary here as data might be updated by some other resources. The
						// listing holds all data received from the server, the data is normalized and connected with other
						// resources via identity (__typename + id), so the changes of the data caused by other resources are
						// propagated, but this field does not know, that cached authorization should not be listed anymore
						// when its state changed from `WAITING` to e.g. `GRANTED` while the listing filter is set to
						// `state|eq|WAITING` - this missing knowledge we must fill in here.
						if (!args?.filter || isEmpty(cached?.data)) {
							return cached;
						}
						const filterKey = args.filter as string;
						let filterMap = authFilterCache.get(cached.data);
						if (!filterMap) {
							filterMap = new Map();
							authFilterCache.set(cached.data, filterMap);
						}
						if (filterMap.has(filterKey)) {
							return filterMap.get(filterKey);
						}
						const result = {
							...cached,
							data: filter(matchesFilter(filterKey, { readField }), cached.data),
						};
						filterMap.set(filterKey, result);
						return result;
					},
				},
				accountAuthorization(cached, { args }) {
					if (!args) return cached;
					const { id } = args;
					return (
						cached ||
						cache.readFragment({
							id: `AuthorizationResponse:${id}`,
							fragment: AccountAuthorizationFragment,
							fragmentName: `AccountAuthorizationFragment`,
						}) ||
						undefined
					);
				},
				coinsTransactions: {
					keyArgs: [`filter`, `sort`],
					merge: mergeFnPaginatedData,
				},
				couponsList: {
					keyArgs: ['filter', 'sort', 'limit'],
					merge: mergeFnPaginatedData,
				},
				myContractDirectDebit: {
					keyArgs: [`filter`, `sort`, `limit`],
					merge: mergeFnPaginatedData,
				},
				myContractApplications: {
					keyArgs: [`filter`, `sort`, `limit`],
					merge: mergeFnPaginatedData,
				},
				myContractBillings: {
					keyArgs: [`filter`, `sort`, `limit`],
					merge: mergeFnPaginatedData,
				},
				myDocuments: {
					keyArgs: ['type', `filter`],
					merge: mergeFnPaginatedData,
				},
				myInvestmentsAccount: {
					keyArgs: [`filter`, `sort`],
					merge: mergeFnPaginatedData,
				},
				myInvestmentsAccountTransactions: {
					keyArgs: [`filter`, `sort`],
					merge: mergeFnPaginatedData,
				},
				myInstallmentsContracts: {
					keyArgs: [`filter`, `sort`, `limit`],
					merge: mergeFnPaginatedData,
				},
				myContractCards: {
					keyArgs: [`filter`],
					merge: mergeFnPaginatedData,
				},
				myContractPaymentRecipients: {
					keyArgs: [`filter`, `sort`, `limit`],
					merge: mergeFnPaginatedData,
				},
				myContractTransactions: {
					keyArgs: [`filter`],
					merge: mergeFnPaginatedData,
				},
				myContractBilling(cached, { args, field }) {
					if (cached) {
						return cached;
					}
					if (!args || !field?.selectionSet) {
						return undefined;
					}
					const { id } = args;
					const { selectionSet } = field;
					const typename = `Billing`;
					const fragmentName = `AdHocBillingFragment`;

					if (Capacitor.isNativePlatform()) {
						try {
							// No need to wait for response from this promise
							debouncedWidgetUpdate();
						} catch {
							// ignore
						}
					}

					try {
						const cachedEntity = cache.readFragment({
							id: `${typename}:${id}`,
							fragment: wrapInDocument([
								selectionSetToFragment(fragmentName, typename, selectionSet),
								...AmountFragment.definitions,
								...BillingDefermentFragment.definitions,
								...BillingFragment.definitions,
							]),
							fragmentName,
						});
						if (cachedEntity) {
							return cachedEntity;
						}
					} catch (error) {
						captureException(new Error(`The Apollo Cache optimization of Billing failed.`), {
							extra: { originalError: error },
						});
					}
					return undefined;
				},
				notifications: {
					keyArgs: [`filter`],
					merge: mergeFnPaginatedData,
				},
				rewardsPartnerPromocodesOffer(cached, { args }) {
					if (!args) return cached;
					const { id } = args;
					return (
						cached ||
						cache.readFragment({
							id: `PartnerPromocodeOffer:${id}`,
							fragment: PromocodeOfferFragment,
							fragmentName: `PromocodeOfferFragment`,
						}) ||
						undefined
					);
				},
				rewardsPartnerPromocodesOffers: {
					keyArgs: [`filter`, `sort`],
					merge: mergeFnPaginatedData,
				},
				rewardsPartnerPromocodesPurchases: {
					keyArgs: [`sort`],
					merge: mergeFnPaginatedData,
				},
				partners: {
					keyArgs: [`filter`, `sort`],
					merge: mergeFnPaginatedData,
				},
				myCashbackAdvertisers: {
					keyArgs: [`filter`, `limit`],
					merge: mergeFnPaginatedData,
				},
				myCashbackOrders: {
					keyArgs: [`limit`],
					merge: mergeFnPaginatedData,
				},
			},
		},
	},
	possibleTypes: possibleTypesFromApiArtifacts(apiArtifacts as ApiArtifacts),
});

async function loadPersistedCache(): Promise<void> {
	try {
		await persistCache({
			cache,
			storage: new LocalStorageWrapper(localStorage),
			maxSize,
			debounce: 3000,
		});
	} catch (error) {
		captureException(new Error('The Apollo Stories Cache persist failed.'), {
			extra: { originalError: error },
		});
	}
}

export const createApolloClient = (
	config?: CreateApolloClientConfig
): ApolloClient<NormalizedCacheObject> => {
	const language =
		config?.language || (store.local.get(LANGUAGE) as AppLanguage | undefined) || AppLanguage.cs;
	const isPersistApolloCacheEnabled = !!store.local.get(STORAGE_KEY_PERSIST_APOLLO_CACHE);

	if (process.env.MOB_APP_BUILD && isPersistApolloCacheEnabled) {
		loadPersistedCache();
	}

	const acceptValidationOnly =
		process.env.NODE_ENV === 'development' ||
		process.env.TARGET_ENV === 'accept' ||
		process.env.TARGET_ENV === 'nightly';

	const httpAgent = createAxiosHttpAgent({
		baseURL: process.env.API_BASE_URL,
		requestMiddlewares: [
			memoPendingRequestsMiddleware({ defaultTTL: 500, operationIdTTL: { myContract: 2000 } }),
			gqlXLanguageMiddleware(language),
			xFieldsRequestMiddleware,
			acceptValidationOnly &&
				// eslint-disable-next-line global-require, @typescript-eslint/no-var-requires
				oasValidationMiddleware(require(`.spec/self-care/openapi.json`) as OpenAPIV3.Document),
		].filter((m): m is RequestMiddleware => Boolean(m)),
	});

	return new ApolloClient({
		cache,
		defaultOptions: {
			watchQuery: {
				nextFetchPolicy(lastFetchPolicy) {
					// this "fixes" weird behavior of apollo hooks with fetch policies `cache-and-network` or `network-only`.
					// E.g. calling `fetchMore` with `cache-and-network` performed two request, one for `fetchMore` data plus one
					// request with initial variables, which was not only inefficient, but it also confused merge function for
					// `fetchMore` result. This converts all subsequent calls to `cache-first` which seems works more naturally.
					// @see https://github.com/apollographql/apollo-client/issues/6916#issuecomment-811203002
					return lastFetchPolicy === `cache-and-network` || lastFetchPolicy === `network-only`
						? `cache-first`
						: lastFetchPolicy;
				},
			},
		},
		link: ApolloLink.from(
			[
				getAuthorizationApolloLink(config?.accessToken),
				process.env.NODE_ENV === 'development' && apolloLogger,
				createApolloLink(cache, apiArtifacts, httpAgent),
			].filter((link): link is ApolloLink => Boolean(link))
		),
		resolvers,
		ssrMode: ssr,
	});
};

export default createApolloClient();
